import type { Item } from "../../../../api/menu";
import {
  getItemName,
  getItemUnitAndSize,
} from "../../../../utils/translations";
import { useTranslation } from "react-i18next";
import { isLunchItem } from "../../../../lib/lunchCatalog";
import { useLunchRoomOf } from "../../lunch/lunchRoomContext";
import { LunchCount, LunchTag } from "../../lunch/LunchTag";
import { highlightMatch, highlightPluCode } from "./highlight";
import {
  applyBillPercentToPrice,
  formatSignedBillPercent,
} from "../billDiscount";
import { tintBackground } from "./catalogColors";
import { CatalogQtyStepper } from "./CatalogQtyStepper";
import type { CatalogCardSizing } from "./catalogLayout";

interface ItemCardProps {
  item: Item;
  selected?: boolean;
  onSelect?: (itemId: number) => void;
  searchTerm?: string;
  quantity?: number;
  onQuantityChange?: (itemId: number, quantity: number) => void;
  // Знаков процент по сметката (клиент с отстъпка/надбавка) — цената се
  // показва като ~~стара~~ ±X% нова; 0 = без корекция
  billDiscountPercent?: number;
  // Цвят на картата по настройката „оцветяване" (станция/група); null = без фон
  tint?: string | null;
  // Как съдържанието се подрежда в размера от дръжките (`catalogLayout.ts`):
  // по съдържанието (без избрана височина), в точната височина или на един ред
  sizing?: CatalogCardSizing;
}

// Праговете на кутийката по собствената ѝ ширина (контейнерът `@container/card`
// е клетката в `CatalogGrid`): в тясна кутийка името и цената са по-дребни и
// първо отпада грамажът (до цената с отстъпка — по-рано, тя е дълга), а на
// един ред — после и цената, ако има „− N +".
const FIT = {
  name: "@max-[190px]/card:text-[14px]",
  pluCode: "@max-[190px]/card:text-[12px]",
  price: "@max-[190px]/card:text-[13px]",
  metaBesideAdjustedPrice: "@max-[215px]/card:hidden",
  // При „− N +" старата цена и процентът отстъпват място на новата; на един
  // ред — винаги в тясна кутийка (името е най-важно).
  oldPriceBesideStepper: "@max-[240px]/card:hidden",
  oneLineOldPrice: "@max-[330px]/card:hidden",
  oneLineMeta: "@max-[260px]/card:hidden",
  oneLinePriceWithStepper: "@max-[230px]/card:hidden",
};

/**
 * Продуктова карта в грид изгледа на каталога (настройка „Грид" от
 * Конфигурация → Маркиране, 0.0.131) — възстановеният изглед отпреди
 * редизайна 0.0.116, преведен на pos-* токените: име на 2 реда, цена и —
 * за да пести място — ИЛИ грамажът (без количество), ИЛИ степерът − N +
 * (при маркирано количество). Кликът (както при реда) добавя +1 — гейтът
 * за задължителни добавки/модификатори отваря „Промени" при нужда. Размерът
 * е от дръжките на избраната кутийка (`CatalogGrid`) и е еднакъв за всички:
 * висока кутийка — името на повече редове, цената и „− N +" долу; ниска
 * (`sizing="oneLine"`) — всичко на един ред.
 */
export const ItemCard = ({
  item,
  selected = false,
  onSelect,
  searchTerm = "",
  quantity = 0,
  onQuantityChange,
  billDiscountPercent = 0,
  tint = null,
  sizing = "content",
}: ItemCardProps) => {
  const { i18n } = useTranslation();
  const itemName = getItemName(item.itemLangs, i18n.language, item.slug);
  const unitAndSize = getItemUnitAndSize(item.itemLangs, i18n.language);
  const hasQty = quantity > 0;
  // Продукт от дневно меню с порции: колко още могат да се добавят (с
  // кошницата); изчерпаният без бройка не се показва. Ограничението е в Ordering.
  const lunchRoomOf = useLunchRoomOf();
  const lunchLeft = lunchRoomOf(item, quantity);
  const lunchRemaining = Number.isFinite(lunchLeft) ? lunchLeft : null;
  const lunchSoldOut = lunchRemaining === 0 && !hasQty;
  // Заключената цена не се коригира от клиентския процент (както в BE)
  const showAdjustedPrice = billDiscountPercent !== 0 && !item.lockedPrice;
  const adjustedPrice = applyBillPercentToPrice(
    item.price,
    billDiscountPercent,
  );

  const oneLine = sizing === "oneLine";
  const fixed = sizing === "fixed";
  // Висока кутийка: името взима мястото над цената; ниска — един ред
  // (`contents` прави частите на двата реда членове на общия ред). На
  // един ред текстовете са по обща базова линия (името 15px и цената в моно
  // 14px, центрирани поотделно, се разминават с ~2px), а „− N +" е
  // центриран до тях.
  const textLineClass = oneLine ? "flex min-w-0 flex-1 items-baseline gap-2" : "contents";
  const nameRowClass = oneLine
    ? "contents"
    : `flex items-start gap-2 ${fixed ? "min-h-0 flex-1 overflow-hidden" : "min-h-[35px]"}`;
  const nameClass = oneLine
    ? "min-w-0 flex-[0_1_auto] truncate"
    : `flex-1 min-w-0 break-words ${fixed ? "line-clamp-[var(--name-lines,2)]" : "line-clamp-2"}`;
  // С избрана височина редът на цената е еднакво висок с грамаж и без него —
  // мястото за името е еднакво във всички кутийки (`useCatalogNameLines`
  // мери само първата).
  const metaRowClass = oneLine
    ? "contents"
    : `flex items-center justify-between gap-2 ${fixed ? "h-5 shrink-0" : ""}`;
  // На един ред грамажът стои само без „− N +" и докато има място; в двата
  // реда остава в потока и при степер (само невидим) — редът на цената
  // пази същата височина и картата не се свива.
  const metaClass = `${
    oneLine ? (hasQty ? "hidden" : FIT.oneLineMeta) : hasQty ? "invisible" : ""
  } ${showAdjustedPrice ? FIT.metaBesideAdjustedPrice : ""}`;
  // „− N +": на един ред — след текстовете, вдясно; в двата реда — ИЗВЪН
  // потока, абсолютно долу вдясно, долният му ръб е на линията на цената,
  // за да не прави картата по-висока (и целия грид ред с нея); нагоре стъпва
  // върху долната част на зоната на името — нарочно, името не се свива.
  // Обеден продукт с височина по съдържанието: етикетът „Обедно" е горе
  // вдясно — степерът се закача на ФИКСИРАНО разстояние под него (31px =
  // мястото му в карта с едноредово име), не за дъното: по-висок грид ред
  // (друга карта с двуредово име) иначе раздалечава етикета и бутоните. С
  // избрана височина всички кутийки са еднакви — степерът е долу при цената.
  const oldPriceClass = oneLine ? FIT.oneLineOldPrice : hasQty ? FIT.oldPriceBesideStepper : "";
  const stepperClass = oneLine
    ? "shrink-0"
    : `absolute right-2 ${isLunchItem(item) && !fixed ? "top-[31px]" : "bottom-1.5"}`;

  return (
    <div
      data-item-card
      onClick={() => onSelect?.(item.id)}
      // Фонът в цвета на станцията/групата идва от данните → inline style.
      // Изчерпан обеден продукт остава сив (цветът би бил bg-pos-line2).
      style={{
        background:
          tint && !lunchSoldOut
            ? tintBackground(tint, hasQty ? 32 : 18)
            : undefined,
      }}
      // Селектираната (keyboard фокусираната) карта леко „изплува" — scale +
      // сянка, както преди редизайна 0.0.116
      aria-disabled={lunchSoldOut || undefined}
      className={`relative flex min-w-0 flex-1 px-2 py-1.5 rounded-xl border cursor-pointer transition-all duration-200 ${
        oneLine ? "items-center gap-2 overflow-hidden" : `flex-col gap-0.5 ${fixed ? "overflow-hidden" : ""}`
      } ${
        lunchSoldOut ? "bg-pos-line2" : hasQty ? "bg-pos-marked" : "bg-pos-card"
      } ${
        selected
          ? // Курсорът = inset пръстен в мастило (както в списъка) + леко
            // „изплуване"; рамката остава неутрална
            // Без оцветяване — кехлибарен пръстен (както преди); при оцветена
            // карта — мастило, което стои върху всеки цвят
            `ring-1 ring-inset ${tint ? "ring-pos-ink" : "ring-pos-accent"} border-pos-line3 z-10 scale-[1.05] shadow-[0_18px_38px_rgba(17,17,17,0.18),0_4px_12px_rgba(17,17,17,0.08)]`
          : hasQty
            ? "border-pos-accent/30"
            : "border-pos-line3 hover:border-pos-accent/35"
      }`}
    >
      <div className={textLineClass}>
        {/* Име — до 2 реда (с избрана височина — колкото се събират), с
            highlight на търсенето */}
        {/* „Обедно" / „Изчерпано" — вдясно на реда с името */}
        <div data-card-name-row className={nameRowClass}>
          <span
            data-card-name
            className={`${nameClass} text-[15px] font-bold leading-[1.3] ${FIT.name} ${
              lunchSoldOut ? "text-pos-muted" : "text-pos-ink"
            }`}
          >
            {highlightMatch(itemName, searchTerm)}
            {item.pluCode != null && (
              <span className={`font-pos-mono text-[13px] font-medium text-pos-muted ${FIT.pluCode}`}>
                {" "}
                | {highlightPluCode(item.pluCode, searchTerm)}
              </span>
            )}
          </span>
          {isLunchItem(item) && (
            <LunchTag
              soldOut={lunchSoldOut}
              label={item.lunchMenuName}
              colorIndex={item.lunchMenuColorIndex}
              className={oneLine ? "" : "mt-px"}
            />
          )}
        </div>

        {/* Мета (цена + грамаж) вляво · − / + вдясно */}
        <div className={metaRowClass}>
          {/* Цената не отстъпва — грамажът до нея се реже/отпада */}
          <span
            className={`flex shrink-0 flex-col gap-px leading-[1.1] ${
              oneLine ? `ml-auto ${hasQty ? FIT.oneLinePriceWithStepper : ""}` : ""
            }`}
          >
            {showAdjustedPrice ? (
              <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
                <span className={`font-pos-mono text-[13px] text-pos-muted line-through opacity-70 ${oldPriceClass}`}>
                  {item.price.toFixed(2)}
                </span>
                <span className={`text-[12px] font-semibold text-pos-disc-ink ${oldPriceClass}`}>
                  {formatSignedBillPercent(billDiscountPercent)}
                </span>
                <span className={`font-pos-mono text-[14px] font-semibold text-pos-ink2 ${FIT.price}`}>
                  {adjustedPrice.toFixed(2)} €
                </span>
              </span>
            ) : (
              <span
                className={`font-pos-mono text-[14px] font-semibold whitespace-nowrap ${FIT.price} ${
                  lunchSoldOut ? "text-pos-muted" : "text-pos-ink2"
                }`}
              >
                {item.price.toFixed(2)} €
              </span>
            )}
          </span>
          {/* Остават „N бр" | грамаж */}
          {(unitAndSize || lunchRemaining != null) && (
            <span
              className={`min-w-0 flex items-center gap-1.5 font-pos-mono text-[12.5px] ${metaClass}`}
            >
              {lunchRemaining != null && <LunchCount count={lunchRemaining} />}
              {lunchRemaining != null && unitAndSize && (
                <span aria-hidden className="h-3 w-px shrink-0 bg-pos-ctl" />
              )}
              {unitAndSize && <span className="truncate text-pos-muted">{unitAndSize}</span>}
            </span>
          )}
        </div>
      </div>
      {hasQty && (
        <span className={stepperClass}>
          <CatalogQtyStepper
            quantity={quantity}
            onDecrement={() =>
              onQuantityChange?.(item.id, Math.max(0, quantity - 1))
            }
            onIncrement={() => onQuantityChange?.(item.id, quantity + 1)}
            incrementDimmed={lunchLeft === 0}
          />
        </span>
      )}
    </div>
  );
};
